import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MagnifyingGlass, ShoppingBag, Tag, Package } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MERCH_CATEGORIES = [
  { value: 'all', label: 'All Items' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'home', label: 'Home & Decor' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'general', label: 'General' },
];

const CONDITION_LABELS = {
  new: 'New', like_new: 'Like New', used: 'Used', refurbished: 'Refurbished',
};

export default function ShopPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const fetchItems = async (params = {}) => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (params.category && params.category !== 'all') qp.set('category', params.category);
      if (params.search) qp.set('search', params.search);
      qp.set('in_stock', 'true');
      const { data } = await axios.get(`${API}/merchandise?${qp.toString()}`);
      setItems(data.items || []);
    } catch (err) {
      console.error('Error fetching merchandise:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchItems({ search, category: category !== 'all' ? category : undefined });
  };

  const handleCategoryChange = (val) => {
    setCategory(val);
    fetchItems({ category: val !== 'all' ? val : undefined, search });
  };

  const placeholderImg = 'https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600';

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-8" data-testid="shop-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#FF6B35] text-white text-xs font-bold px-4 py-1.5 rounded-full border-2 border-[#004E89] shadow-[2px_2px_0px_#004E89] mb-4">
            <ShoppingBag size={14} weight="bold" />
            PROMOTIONAL ITEMS
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>
            The Shop
          </h1>
          <p className="text-[#5C7A99] text-base md:text-lg max-w-md mx-auto">
            Exclusive promotional items curated by our team
          </p>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-wrap gap-3 mb-8 max-w-2xl mx-auto">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                className="input-neo pl-10 py-2.5 text-sm" placeholder="Search items..."
                data-testid="shop-search-input"
              />
            </div>
            <button type="submit" className="btn-primary py-2 px-4" data-testid="shop-search-btn">
              <MagnifyingGlass size={18} weight="bold" />
            </button>
          </form>
          <Select value={category} onValueChange={handleCategoryChange}>
            <SelectTrigger className="border-2 border-[#004E89] rounded-xl shadow-[2px_2px_0px_#004E89] h-11 text-xs font-bold w-[150px]" data-testid="shop-category-filter">
              <SelectValue placeholder="All Items" />
            </SelectTrigger>
            <SelectContent className="border-2 border-[#004E89] rounded-xl shadow-[4px_4px_0px_#004E89]">
              {MERCH_CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        <div className="text-xs font-bold text-[#5C7A99] uppercase tracking-wide mb-4" data-testid="shop-results-count">
          {loading ? 'Loading...' : `${items.length} item${items.length !== 1 ? 's' : ''} available`}
        </div>

        {items.length === 0 && !loading ? (
          <div className="text-center py-20" data-testid="shop-no-items">
            <Package size={56} weight="duotone" className="mx-auto text-[#5C7A99] mb-4" />
            <p className="text-lg font-bold text-[#004E89] mb-2">No items available yet</p>
            <p className="text-[#5C7A99] text-sm">Check back soon for new promotional items</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {items.map((item, i) => (
              <Link key={item.id} to={`/shop/${item.id}`} className="block" data-testid={`shop-item-${item.id}`}>
                <div className="sale-card slide-in h-full" style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="w-full h-48 rounded-xl overflow-hidden border-2 border-[#004E89]">
                    {item.photos?.length > 0 ? (
                      <img src={`${API}/files/${item.photos[0]}`} alt={item.title}
                        className="w-full h-full object-cover" onError={(e) => { e.target.src = placeholderImg; }} />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#FFD166] to-[#FF6B35] flex items-center justify-center">
                        <ShoppingBag size={48} weight="duotone" color="#004E89" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="tag tag-estate text-[10px]">{CONDITION_LABELS[item.condition] || item.condition}</span>
                    {item.category !== 'general' && (
                      <span className="tag bg-[#F9F9F6] text-[#5C7A99] border-[#5C7A99] text-[10px]">{item.category}</span>
                    )}
                  </div>
                  <h3 className="text-base font-bold text-[#004E89] line-clamp-1" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                  <p className="text-sm text-[#5C7A99] line-clamp-2">{item.description}</p>
                  <div className="mt-auto pt-2 flex items-center justify-between">
                    <span className="text-xl font-black text-[#FF6B35]" style={{ fontFamily: 'Outfit' }}>${item.price.toFixed(2)}</span>
                    {item.shipping_info && (
                      <span className="text-[10px] text-[#5C7A99] font-medium">{item.shipping_info}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
